<script>

import useSharedData from "@/use/SharedData";
import {computed, ref, watch} from "vue";
import WMenuValidationAction from "@/components/WMenuValidationAction.vue";
import {useSecurityStore} from "@/stores/Security";
import useSharedMethod from "@/use/SharedMethod";

export default {
    name: "WServerDataTable",
    components: {WMenuValidationAction},
    props: {
        value: Object,
        headers: Array,
        items: Array,
        actions: Array,
        total: Number,
        loading: Boolean,
        fixedHeader: Boolean,
        height: String,
        selectFirst: Boolean,
        showDefaultItemColor: Boolean,
        showExpand: Boolean,
        showAllExpanded: Boolean,
        singleExpand: Boolean,
        expandIcon: String,
        showValidation: Boolean,
        hideColors: Boolean,
        rootPath: String,
        module: String,
    },
    emits: ["rowDbClick", "itemClick", "itemExpanded"],
    setup(props, {emit}) {
        const securityStore = useSecurityStore();
        const {formatedDate} = useSharedMethod();
        const {
            page,
            itemsPerPage,
            paginationListLength,
            itemColor,
            tableElevation
        } = useSharedData();
        
        const expanded = ref([]);
        
        const selectedOne = ref({id: null})
        
        const sortBy = ref(props.value.sortBy);
        const descending = ref(props.value.descending);
        
        page.value = props.value.page;
        itemsPerPage.value = props.value.itemsPerPage;
        
        const transformedHeaders = computed(() => {
            const removeFirst = !Array.isArray(props.actions) ||
                props.actions.every(action => !action.access);
            
            const headers = removeFirst
                ? props.headers.slice(1) // drop the first header
                : props.headers;
            
            return headers.map(header => ({
                ...header,
                class: 'text-no-wrap'
            }));
        });
        
        function updateSearchParams() {
            const searchParams = {
                sortBy: sortBy.value,
                descending: descending.value,
                page: page.value,
                itemsPerPage: itemsPerPage.value
            };
            
            // Emit the updated searchParams as v-model
            emit('input', searchParams);
        }
        
        function rowDbClick(event, {item}) {
            emit("rowDbClick", item);
        }
        
        function rowColor(item) {
            if(props.hideColors)
                return;
            
            if (item.id === selectedOne.value.id)
                return itemColor.value;
            
            if (item.color)
                return item.color;
            
            if (item.validateOption || item.takeInChargeOption)
                return securityStore.userContext.validationColor;
            
            if (props.showDefaultItemColor)
                return "brown lighten-5";
        }
        
        function itemClick(item) {
            selectedOne.value = item;
            emit('itemClick', item);
        }
        
        function itemExpanded({item, value}) {
            itemClick(item);
            emit('itemExpanded', {item, value});
        }
        
        watch([sortBy, descending], () => {
            updateSearchParams();
        });
        
        let firstOne = {};
        watch(() => props.items, () => {
            if (props.showAllExpanded)
                expanded.value = props.items;
            if (props.selectFirst) {
                const [firstItem] = props.items;
                if (firstItem?.id !== firstOne.id) { // Optional chaining for safety
                    firstOne = firstItem ?? {id: null}; // Fallback to `{ id: null }` if undefined
                    itemClick(firstOne);
                }
            }
        }, {deep: true, immediate: true});
        
        
        watch(() => props.value, (newValue) => {
            sortBy.value = newValue.sortBy;
            descending.value = newValue.descending;
            page.value = newValue.page;
            itemsPerPage.value = newValue.itemsPerPage;
        }, {deep: true});
        
        return {
            tableElevation,
            page,
            itemsPerPage,
            paginationListLength,
            sortBy,
            descending,
            expanded,
            transformedHeaders,
            updateSearchParams,
            rowColor,
            itemClick,
            rowDbClick,
            itemExpanded,
            formatedDate,
        }
    }
}
</script>

<template>
    <div>
        <!--<v-skeleton-loader
            v-if="loading"
            ref="skeleton"
            :type="'table'"
            class="mx-auto"
        ></v-skeleton-loader>-->
        <v-data-table
            :headers="transformedHeaders"
            :items="items"
            :sort-by.sync="sortBy"
            :sort-desc.sync="descending"
            :server-items-length="total"
            :loading="loading"
            must-sort
            :multi-sort="false"
            hide-default-footer
            dense
            :fixed-header="fixedHeader"
            :height="height"
            :show-expand="showExpand"
            :single-expand="singleExpand"
            :expand-icon="expandIcon"
            :expanded.sync="expanded"
            :item-class="rowColor"
            @click:row="itemClick"
            @dblclick:row="rowDbClick"
            @item-expanded="itemExpanded"
        >
            
            <!-- Dynamic header slots -->
            <template v-for="header in headers" v-slot:[`item.${header.value}`]="{ item }">
                <slot
                    :name="`item.${header.value}`"
                    :item="item"
                >
                    {{ item[header.value] }}
                </slot>
            </template>
            
            <template
                v-if="actions"
                v-slot:[`item.action`]="{ item }"
            >
                <v-menu bottom right>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            dark
                            icon
                            color="blue darken-2"
                            v-on="on"
                            @click="itemClick(item)"
                        >
                            <v-icon>mdi-dots-horizontal</v-icon>
                        </v-btn>
                    </template>
                    
                    <v-list nav dense>
                        <w-menu-validation-action
                            v-if="showValidation"
                            :item="item"
                            :rootPath="rootPath"
                            :module="module"
                        />
                        <template v-for="(action, index) in actions">
                            <v-list-item
                                :key="`action-${index}`"
                                @click="action.func(item)"
                                v-if="action.access(item)"
                                :disabled="action.disabled(item)"
                            >
                                <v-list-item-icon>
                                    <v-icon :color="action.color">{{ action.icon }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    {{ action.title }}
                                </v-list-item-title>
                            </v-list-item>
                        </template>
                    </v-list>
                </v-menu>
            </template>
            
            <template v-slot:[`item.validationStatus.lastState`]="{ item }">
                <w-chip
                    outlined
                    :text="item.validationStatus.autoValidate ? 'Validation Automatique' : item.validationStatus.lastState"
                />
            </template>
            <template v-slot:[`item.validationStatus.lastValidationDate`]="{ item }">
                <w-chip
                    outlined
                    :text="item.validationStatus.lastValidationDate ? formatedDate(item.validationStatus.lastValidationDate) : formatedDate(item.postDateTime)"
                />
            </template>
            <template v-slot:[`item.validationStatus.nextValidator`]="{ item }">
                <w-chip
                    v-if="item.validationStatus.nextValidator"
                    outlined
                    :text="item.validationStatus.nextValidatorFullName"
                />
            </template>
            
            <template v-slot:expanded-item="{ headers, item }">
                <slot
                    name="expanded-item"
                    :item="item"
                    :headers="headers"
                >
                </slot>
            </template>
            
            <!-- Footer slot -->
            <template v-slot:footer="{}">
                <slot name="footer">
                    <br/>
                    <div class="text-center">
                        <v-row justify="center">
                            <v-col md="6">
                                <v-pagination
                                    v-model="page"
                                    :length="Math.ceil(total / itemsPerPage)"
                                    total-visible="15"
                                    @input="updateSearchParams"
                                />
                            </v-col>
                            <v-col md="2">
                                <w-enum-select
                                    v-model="itemsPerPage"
                                    :items="paginationListLength"
                                    :label="$t('common.items_per_page')"
                                    not-clearable
                                    @change="updateSearchParams"
                                />
                            </v-col>
                        </v-row>
                    </div>
                </slot>
            </template>
        </v-data-table>
        
        <input type="hidden">
    </div>
</template>
